import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

// pdfjs-dist provides its own types
import { getDocument, GlobalWorkerOptions, Util, type PDFDocumentProxy, type PDFPageProxy } from 'pdfjs-dist';
// Vite-friendly worker URL
// eslint-disable-next-line import/no-unresolved
import workerSrc from 'pdfjs-dist/build/pdf.worker?url';

GlobalWorkerOptions.workerSrc = workerSrc;

type Props = {
  url: string;
  highlightText?: string | null;
  height?: string | number;
};

function normalizeHighlights(raw?: string | null): string[] {
  const v = (raw ?? '').trim();
  if (!v) return [];

  // If user typed a phrase, also highlight individual tokens so it still works across PDF text runs.
  const tokens = v
    .split(/\s+/g)
    .map(t => t.trim())
    .filter(t => t.length >= 2);

  // Keep order, unique (case-insensitive)
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of [v, ...tokens]) {
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

function textItemContainsAny(text: string, needles: string[]): boolean {
  const hay = (text ?? '').toLowerCase();
  for (const n of needles) {
    if (!n) continue;
    if (hay.includes(n.toLowerCase())) return true;
  }
  return false;
}

async function renderPageToCanvas(page: PDFPageProxy, scale: number, canvas: HTMLCanvasElement) {
  const viewport = page.getViewport({ scale });
  const ctx = canvas.getContext('2d');
  if (!ctx) return { viewport };

  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);

  await page.render({ canvasContext: ctx, viewport, canvas } as any).promise;
  return { viewport };
}

async function renderHighlights(page: PDFPageProxy, scale: number, overlay: HTMLCanvasElement, needles: string[]) {
  const viewport = page.getViewport({ scale });
  const ctx = overlay.getContext('2d');
  if (!ctx) return;

  overlay.width = Math.ceil(viewport.width);
  overlay.height = Math.ceil(viewport.height);

  ctx.clearRect(0, 0, overlay.width, overlay.height);
  if (!needles.length) return;

  const textContent = await page.getTextContent();

  // Use a conservative highlight style that fits the existing palette.
  // (No theme tokens exist for highlight; this matches existing warm accent usage.)
  ctx.fillStyle = 'rgba(247, 201, 72, 0.55)';

  // Transform helper exists on the page viewport.
  const vt = viewport.transform;

  for (const item of textContent.items as any[]) {
    const str: string = item?.str ?? '';
    if (!str) continue;
    if (!textItemContainsAny(str, needles)) continue;

    // Convert PDF text item transform to viewport coordinates.
    // Based on PDF.js recommended approach: multiply viewport.transform by item.transform.
    const m = Util.transform(vt as any, item.transform as any) as number[];

    const x = m[4];
    const y = m[5];

    // Approximate font height from transform.
    const fontHeight = Math.max(2, Math.hypot(m[2], m[3]));

    // item.width is in text space; scale it.
    const w = Math.max(2, (item.width ?? 0) * scale);

    // y is baseline; highlight above baseline.
    const rectY = y - fontHeight;

    ctx.fillRect(x, rectY, w, fontHeight);
  }
}

function PdfPage({ page, scale, needles }: { page: PDFPageProxy; scale: number; needles: string[] }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!canvasRef.current || !overlayRef.current) return;
      await renderPageToCanvas(page, scale, canvasRef.current);
      if (cancelled) return;
      await renderHighlights(page, scale, overlayRef.current, needles);
    })().catch(() => {
      // ignore; parent shows error
    });

    return () => {
      cancelled = true;
    };
  }, [page, scale, JSON.stringify(needles)]);

  return (
    <div style={{ position: 'relative', width: 'fit-content', margin: '0 auto 12px auto' }}>
      <canvas ref={canvasRef} style={{ display: 'block' }} />
      <canvas
        ref={overlayRef}
        style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
      />
    </div>
  );
}

export function PdfCanvasViewer({ url, highlightText, height = '85vh' }: Props) {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);

  const needles = useMemo(() => normalizeHighlights(highlightText), [highlightText]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setDoc(null);

    (async () => {
      try {
        const task = getDocument({ url });
        const loaded = await task.promise;
        if (cancelled) return;
        setDoc(loaded);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load PDF');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  // A conservative scale that fits most layouts; modal is wide.
  const scale = 1.25;

  return (
    <div style={{ width: '100%', height, overflow: 'auto', background: '#fff' }}>
      {error && <div style={{ padding: 12, color: 'red' }}>{error}</div>}
      {!error && !doc && <div style={{ padding: 12 }}>Loading PDF…</div>}
      {doc && (
        <div style={{ padding: 10 }}>
          {Array.from({ length: doc.numPages }, (_, idx) => idx + 1).map(pageNo => (
            <PdfPageWrapper key={pageNo} doc={doc} pageNo={pageNo} scale={scale} needles={needles} />
          ))}
        </div>
      )}
    </div>
  );
}

function PdfPageWrapper({ doc, pageNo, scale, needles }: { doc: PDFDocumentProxy; pageNo: number; scale: number; needles: string[] }) {
  const [page, setPage] = useState<PDFPageProxy | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await doc.getPage(pageNo);
      if (cancelled) return;
      setPage(p);
    })().catch(() => {
      // ignore
    });

    return () => {
      cancelled = true;
    };
  }, [doc, pageNo]);

  if (!page) return <div style={{ padding: 8 }}>Loading page {pageNo}…</div>;
  return <PdfPage page={page} scale={scale} needles={needles} />;
}
